import { Component, computed, inject, Input, LOCALE_ID, OnInit, signal } from '@angular/core';
import { arabicNameValidators, optionalName } from '../../../../validators/name-rules';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SubcategoryService } from '../../../../services/subcategory/subcategory.service';
import { SubcategoryFileMgt } from '../../../../services/subcategory/fileMgt.subcategory.service';
import { Router } from '@angular/router';
import { SubcategoriesListRequest } from '../../../../pages/admin-dashboard/admin-subcategories/admin-subcategories.model';
import { CommonModule } from '@angular/common';
import { CategoryVTO } from '../../../../pages/admin-dashboard/categories/category.model';
import { CategoryService } from '../../../../services/category/category.service';

@Component({
  selector: 'app-edit-subcategories-form',
  imports: [ReactiveFormsModule, CommonModule, FormsModule],
  templateUrl: './edit-subcategories-form.html',
  styleUrl: './edit-subcategories-form.scss',
})
export class EditSubcategoriesForm implements OnInit {
  private locale = inject(LOCALE_ID);
  isArabic = computed(() => this.locale.startsWith('ar'));
  @Input() subcategoryId!: number;

  private subcategoryService = inject(SubcategoryService);
  private subcategoryFileMgt = inject(SubcategoryFileMgt);
  private categoryService = inject(CategoryService);
  private router = inject(Router);

  categories = signal<CategoryVTO[]>([]);
  loading = signal<boolean>(false);
  loadingSubcategory = signal<boolean>(false);
  errorUpdatingSubcategory = signal<string | null>(null);

  selectedImage: File | null = null;
  imagePreview: string | null = null;
  existingImageFid: string | null = null;
  formSubmitted = false;
  isDragging = false;
  isActive = false;

  subcategoryForm = new FormGroup({
    titleEn: new FormControl('', [Validators.minLength(2), Validators.maxLength(100)]),
    titleAr: new FormControl('', arabicNameValidators(100)),
    descriptionEn: new FormControl('', [Validators.maxLength(4000)]),
    descriptionAr: new FormControl('', [Validators.maxLength(4000)]),
    categoryId: new FormControl<number | null>(null, [Validators.required]),
    profileImageFid: new FormControl(''),
  });

  ngOnInit() {
    if (this.subcategoryId) {
      this.loadSubcategory();
    }

    this.categoryService.getAllCategories({ orderBy: 'CREATION_DATE' }).subscribe({
      next: (res) => {
        // Categories loaded
        this.categories.set(res.list);
      },
      error: () => {},
    });
  }

  get selectedCategoryTitle(): string | undefined {
    const categoryId = this.subcategoryForm.get('categoryId')?.value;
    // console.log('Selected Category ID:', categoryId);
    const selectedCat = this.categories().find((c) => c.id === categoryId);
    // console.log('Selected Category:', selectedCat);
    return this.isArabic() ? selectedCat?.titleAr : selectedCat?.titleEn;
  }

  loadSubcategory() {
    this.loadingSubcategory.set(true);
    this.subcategoryService.getSubcategoryById(this.subcategoryId).subscribe({
      next: (subcategory: any) => {
        this.subcategoryForm.patchValue({
          titleEn: subcategory.titleEn,
          titleAr: subcategory.titleAr || '',
          descriptionEn: subcategory.descriptionEn || '',
          descriptionAr: subcategory.descriptionAr || '',
          categoryId: subcategory.category.id || null,
          profileImageFid: subcategory.profileImageFid || '',
        });
        this.existingImageFid = subcategory.profileImageFid || null;
        this.loadingSubcategory.set(false);
      },
      error: (err: Error) => {
        console.error('Error loading category:', err);
        this.loadingSubcategory.set(false);
        this.errorUpdatingSubcategory.set('Failed to load category. Please try again.');
      },
    });
  }

  onFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    this.processFile(file);

    input.value = '';
  }

  processFile(file: File) {
    if (!file.type.match(/image\/(png|jpe?g)/)) {
      alert($localize`:@@subcategoryFormFileTypeError:Only PNG and JPG files are allowed`);
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert($localize`:@@subcategoryFormFileSizeError:File ${file.name} exceeds 5MB limit`);
      return;
    }

    this.selectedImage = file;

    const reader = new FileReader();
    reader.onload = (e) => {
      this.imagePreview = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFile(files[0]);
    }
  }

  removeImage() {
    this.selectedImage = null;
    this.imagePreview = null;
    this.existingImageFid = null;
    this.subcategoryForm.patchValue({ profileImageFid: '' });
  }

  onSubmit() {
    this.formSubmitted = true;

    if (this.subcategoryForm.invalid) {
      for (const key in this.subcategoryForm.controls) {
        this.subcategoryForm.get(key)?.markAsTouched();
      }
      return;
    }

    this.loading.set(true);
    this.errorUpdatingSubcategory.set(null);

    if (this.selectedImage) {
      this.subcategoryFileMgt.uploadImage(this.selectedImage).subscribe({
        next: (result: { fid: string }) => {
          this.updateCategory(result.fid);
        },
        error: (error: Error) => {
          console.error('Error uploading image:', error);
          this.loading.set(false);
          this.errorUpdatingSubcategory.set('Failed to upload image. Please try again.');
        },
      });
    } else {
      this.updateCategory(this.existingImageFid || '');
    }
  }

  private updateCategory(imageFid: string) {
    const formValue = this.subcategoryForm.getRawValue();
    const payload: SubcategoriesListRequest = {
      titleEn: optionalName(formValue.titleEn),
      titleAr: formValue.titleAr!.trim(),
      descriptionEn: formValue.descriptionEn!,
      descriptionAr: formValue.descriptionAr!,
      profileImageFid: imageFid,
      isActive: this.isActive,
      categoryId: formValue.categoryId!,
    };

    this.subcategoryService.updateSubcategory(this.subcategoryId, payload).subscribe({
      next: () => {
        this.loading.set(false);
        this.router.navigate(['/admin/subcategories']);
      },
      error: (err: Error) => {
        console.error('Error updating subcategory:', err);
        this.loading.set(false);
        this.errorUpdatingSubcategory.set('Failed to update subcategory. Please try again.');
      },
    });
  }

  onCancel() {
    this.router.navigate(['/admin/subcategories']);
  }
}
